import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import { hesaplaHacim } from '../FikoYT-Fonksiyonlar';
import { getDokumMalzemeDB, DokumAlasimKategori, KalipMalzemeKategori } from '../fiko-sph-sivi/FikoDokumMalzemeDB';
import { methodingHesapla as methodingHesaplaFn } from './FikoMethodingHesap';
import { voxelle, kafesGenislet } from './simulasyon/FikoVoxelKafes';
import { FikoDolumSimulasyon } from './simulasyon/FikoDolumSimulasyon';
import { FikoKatilasmaSimulasyon } from './simulasyon/FikoKatilasmaSimulasyon';
import type { IKatilasmaSonuc } from './simulasyon/IKatilasmaSim';
import { voxelIndeks, voxelMerkez, type IVoxelKafes, type IDolumSonuc } from './simulasyon/IDokumSim';
import { sicaklikLookup, katilasmaFraksiyonu } from '../fiko-sph-sivi/FikoDokumMalzemeDB';
import { FikoSPHMotor } from '../fiko-sph-sivi/FikoSPHMotor';
import { FikoSPHVoxelKopru, kafestenSDF, DOLMADI_U32 } from '../fiko-sph-sivi/gpu/FikoSPHVoxelKopru';
import type { IDolumSimSonuc, CozumKalitesi } from './IDokumKontrol';
import { FikoDokumGostergeler } from './FikoDokumGostergeler';
import { FikoYollukYonetici } from './FikoYollukYonetici';
import {
  IDokumKontrolMotor, IDokumKontrolGirdi, IKontrolSonuc,
  IGostergeSonuc, ICozumGeometri, IGostergeAyar, IDokumNotSonuc,
  IYollukEleman, IYollukGuncelleme, IMethodingSonuc, YollukTipi, ISensorVeri, VARSAYILAN_GOSTERGE_AYAR,
  BESLEYICI_TIP_H_CARPAN, SOGUTUCU_H_CARPAN,
} from './IDokumKontrol';

/** Alaşıma göre minimum güvenli cidar (mm) — eşikleri belirler */
const ALASIM_MIN_CIDAR: Record<string, number> = {
  'AlSi (döküm alüminyum)': 2.5,
  'Al': 2.5,
  'Pik (gri dökme demir)': 3.0,
  'GGG (sfero)': 3.0,
  'Çelik': 4.0,
  'Mg': 2.0,
  'Zn (Zamak)': 1.0,
  'Pirinç/Bronz': 2.5,
};

const VARSAYILAN_ALASIM = 'AlSi (döküm alüminyum)';

/** Alaşım kategorisi → elastik özellikler (E GPa, α 1/K, sıcak akma MPa) — gerilim MPa tahmini */
const KATEGORI_ELASTIK: Record<string, { E: number; alfa: number; akma: number }> = {
  aluminyum: { E: 70, alfa: 22e-6, akma: 180 },
  demir_dokum: { E: 170, alfa: 12e-6, akma: 250 },
  celik_dokum: { E: 200, alfa: 13e-6, akma: 300 },
  bakir: { E: 110, alfa: 17e-6, akma: 150 },
  magnezyum: { E: 45, alfa: 26e-6, akma: 120 },
  cinko: { E: 96, alfa: 27e-6, akma: 200 },
};

/**
 * Döküm Kontrol orkestratörü.
 * Akış: mesh birleştir (dünya uzayı) → voxel doluluk → cidar kalınlığı (GPU) →
 *       gösterge motoru → not. Yolluk öner/ekle/kabul/sil yönetimi.
 */
export class FikoDokumKontrol implements IDokumKontrolMotor {
  private gostergeMotoru = new FikoDokumGostergeler();
  private yollukMotoru = new FikoYollukYonetici();

  private sonGosterge: IGostergeSonuc | null = null;
  private sonGeo: ICozumGeometri | null = null;
  private sonKalinlik: Float32Array | null = null;
  private sonMeshler: THREE.Mesh[] = [];
  private sonKafes: IVoxelKafes | null = null;
  /** Dolum CA'nın kullandığı (gerekirse kabalaştırılmış) kafes — katılaşma+görsel aynı olmalı */
  private sonDolumKafes: IVoxelKafes | null = null;
  /** Son dolum + katılaşma sonuçları (sensör okuması için, sonDolumKafes ile hizalı) */
  private sonDolumSonuc: IDolumSonuc | null = null;
  private sonKatilasmaSuresi: Float32Array | null = null;
  /** Kullanıcı voxel çözünürlüğü (0 = otomatik/kabalaştır) */
  private dolumCozunurluk = 0;
  /** Döküm prosesi override: döküm sıcaklığı (0 = alaşım vars.) + kalıp ön ısıtma (°C) */
  private dokumSicaklikOverride = 0;
  private kalipSicaklikOverride = 25;
  private yukari = new THREE.Vector3(0, 1, 0);
  private sonAlasim = VARSAYILAN_ALASIM;
  private _yolluklar: IYollukEleman[] = [];
  /** Kafese damgalanan besleyiciler (kafesGenislet sırasıyla — besleyiciIdx hizalı) */
  private sonBesleyiciler: IYollukEleman[] = [];
  /** Parametrik malzeme override — DB alaşımının üstüne yazılır (her şey ayarlanabilir) */
  private alasimOverride: Record<string, number> = {};
  /** Çözüm kalitesi: 'onizleme' = CA (hızlı JS), 'final' = SPH WebGPU parçacık dolumu */
  private cozumKalitesi: CozumKalitesi = 'onizleme';

  constructor(private anaGosterim?: any) {}

  async kontrolEt(girdi: IDokumKontrolGirdi): Promise<IKontrolSonuc> {
    const ilerle = girdi.onProgress ?? (() => {});
    if (!girdi.meshler?.length) throw new Error('Kontrol için mesh bulunamadı');

    this.sonAlasim = girdi.alasim || VARSAYILAN_ALASIM;
    this.sonMeshler = girdi.meshler.slice();
    ilerle(3);

    const birlesik = this.meshleriBirlestir(girdi.meshler);
    const koseUzunluk = birlesik.geo.bbox.getSize(new THREE.Vector3()).length();
    ilerle(12);

    birlesik.geo.yukariYon = this.yukari.clone();
    // Parçayı voxelle (kavite) — SDF kalınlık (iç+dış tutarlı, gerçek duvar) +
    // Dolum simülasyonunun yeniden voxellememesi için sakla.
    this.sonKafes = voxelle({
      positions: birlesik.geo.positions,
      indices: birlesik.geo.indices,
      bbox: birlesik.geo.bbox,
      cozunurluk: 200, // ~3mm voxel (scanline ucuz)
      hacimCm3Kesin: this.malzemeHacimCm3(),
      ilerle: (p) => ilerle(12 + p * 0.45),
    });
    ilerle(58);
    // Cidar kalınlığı (voxel SDF'ten örnekle) + tavan mesafesi (ışın)
    const alanlar = await this.bvhAlanlariHesapla(birlesik.geo, koseUzunluk, this.sonKafes, ilerle);
    const kalinlikAlani = alanlar.kalinlik;
    birlesik.geo.tavanMesafe = alanlar.tavan;
    ilerle(82);

    const ayar: IGostergeAyar = {
      ...VARSAYILAN_GOSTERGE_AYAR,
      minCidarMm: this.minCidar(this.sonAlasim),
      ...(girdi.ayar || {}),
    };

    const gosterge = this.gostergeMotoru.hesapla(birlesik.geo, kalinlikAlani, ayar);
    ilerle(100);

    this.sonGosterge = gosterge;
    this.sonGeo = birlesik.geo;
    this.sonKalinlik = kalinlikAlani;
    // Yerleştirilen yolluklar korunur; yeni göstergeye göre yeniden doğrulanır.
    if (this._yolluklar.length) {
      for (const e of this._yolluklar) {
        const d = this.yollukMotoru.dogrula(e, gosterge, birlesik.geo);
        e.gecerli = d.gecerli; e.dogrulamaMesaji = d.mesaj;
      }
      this._yolluklar = [...this._yolluklar];
    }

    // Yolluk kapsaması: besleyici/hava deliği/giriş bölgeleri çözüyor mu → not'a yansıt
    this.kapsamaUygula();
    const not = this.gostergeMotoru.notHesapla(gosterge);

    this.birlesikTemizle(birlesik.mesh);
    return { gosterge, not, geo: birlesik.geo };
  }

  /**
   * Yerleştirilen yollukların hangi bölgeleri çözdüğünü işaretle (voxel YOK).
   * Mühendislik kapsama kuralları:
   *  • besleyici  → sıcak nokta (geç katılaşan kütleyi besler, çekmeyi önler)
   *  • hava deliği → hapsolan hava (cebe sıkışan gazı tahliye eder)
   *  • dolum girişi → ince cidar (yakın giriş ince kesitin dolmasını kolaylaştırır)
   *  • ani kesit → yollukla çözülmez (radüs/geometri gerekir)
   * Erişim mesafesi parça-göreli (bbox köşegeni) + yolluk çapına oranlı.
   */
  private kapsamaUygula(): void {
    const g = this.sonGosterge, geo = this.sonGeo;
    if (!g || !geo) return;
    const kose = geo.bbox.getSize(new THREE.Vector3()).length() || 1;
    const taban = kose * 0.07; // temel erişim yarıçapı

    const aktif = this._yolluklar.filter(y => y.kabulEdildi !== false);
    const tipPoz = (t: YollukTipi) => aktif.filter(y => y.tip === t);
    const besleyiciler = tipPoz('besleyici');
    const sogutucular = tipPoz('sogutucu');
    const delikler = tipPoz('hava_deligi');
    const girisler = tipPoz('dolum_girisi');

    const kapsiyorMu = (
      list: IYollukEleman[], merkez: THREE.Vector3,
    ): IYollukEleman | null => {
      let enYakin: IYollukEleman | null = null, enKisa = Infinity;
      for (const y of list) {
        const erisim = taban + (y.capMm || 20) * 1.5;
        const d = y.pozisyon.distanceTo(merkez);
        if (d <= erisim && d < enKisa) { enKisa = d; enYakin = y; }
      }
      return enYakin;
    };

    for (const b of g.bolgeler) {
      let yolluk: IYollukEleman | null = null;
      let mesaj = '';
      if (b.tip === 'sicak_nokta') {
        yolluk = kapsiyorMu(besleyiciler, b.merkez);
        if (yolluk) { mesaj = 'Besleyici besliyor — çekme riski giderildi.'; }
        else {
          yolluk = kapsiyorMu(sogutucular, b.merkez);
          if (yolluk) mesaj = 'Soğutucu hızlı katılaştırıyor — sıcak nokta giderildi.';
        }
      } else if (b.tip === 'hapsolan_hava') {
        yolluk = kapsiyorMu(delikler, b.merkez);
        if (yolluk) mesaj = 'Hava deliği tahliye ediyor.';
      } else if (b.tip === 'ince_cidar') {
        yolluk = kapsiyorMu(girisler, b.merkez);
        if (yolluk) mesaj = 'Dolum girişi yakın — kesit dolar.';
      }
      b.cozuldu = !!yolluk;
      b.cozumMesaji = mesaj;
      if (yolluk && !yolluk.hedefBolgeId) yolluk.hedefBolgeId = b.id;
    }
  }

  yollukKapsamaGuncelle(): { gosterge: IGostergeSonuc; not: IDokumNotSonuc } | null {
    if (!this.sonGosterge) return null;
    this.kapsamaUygula();
    return { gosterge: this.sonGosterge, not: this.gostergeMotoru.notHesapla(this.sonGosterge) };
  }

  dolumCozunurlukAyarla(n: number): void {
    this.dolumCozunurluk = n > 0 ? Math.round(n) : 0;
  }

  /** Çözüm kalitesi: 'onizleme' (CA) | 'final' (SPH WebGPU). WebGPU yoksa CA'ya düşer. */
  cozumKalitesiAyarla(k: CozumKalitesi): void {
    this.cozumKalitesi = k === 'final' ? 'final' : 'onizleme';
  }

  prosesAyarla(ayar: { dokumSicaklikC?: number; kalipSicaklikC?: number }): void {
    if (ayar.dokumSicaklikC != null) this.dokumSicaklikOverride = ayar.dokumSicaklikC > 0 ? ayar.dokumSicaklikC : 0;
    if (ayar.kalipSicaklikC != null) this.kalipSicaklikOverride = ayar.kalipSicaklikC;
  }

  /** Parametrik malzeme: sayısal alaşım alanlarını DB değerinin üstüne yaz (0/NaN = alanı sıfırla). */
  alasimAyarla(override: Record<string, number>): void {
    this.alasimOverride = { ...override };
  }

  /** DB alaşımı + kullanıcı override birleşimi (parametrik malzeme editörü). */
  private alasimCoz(alasimId: string): any {
    const db = getDokumMalzemeDB();
    const al = db.alasimAl(alasimId)
      ?? db.kategoriAlasimlar(DokumAlasimKategori.ALUMINYUM)[0]
      ?? db.tumAlasimlar()[0];
    if (!al) return null;
    const anahtarlar = Object.keys(this.alasimOverride);
    if (!anahtarlar.length) return al;
    const kopya: any = { ...al };
    for (const k of anahtarlar) {
      const v = this.alasimOverride[k];
      if (v != null && isFinite(v)) kopya[k] = v;
    }
    return kopya;
  }

  /** Etkin döküm sıcaklığı (override veya alaşım ortalaması). */
  private dokumSicaklik(al: { T_dokum_min: number; T_dokum_max: number }): number {
    return this.dokumSicaklikOverride > 0 ? this.dokumSicaklikOverride : (al.T_dokum_min + al.T_dokum_max) / 2;
  }

  yukariYonAyarla(yukari: THREE.Vector3): void {
    if (yukari && yukari.lengthSq() > 1e-6) this.yukari.copy(yukari).normalize();
  }

  yukariYonTersine(): THREE.Vector3 {
    this.yukari.negate();
    return this.yukari.clone();
  }

  async dolumSimulasyonu(alasimId: string, ilerle?: (y: number) => void): Promise<IDolumSimSonuc | null> {
    if (!this.sonGeo) return null;
    const alasim = this.alasimCoz(alasimId);
    if (!alasim) return null;

    let kafes: IVoxelKafes;
    if (this.dolumCozunurluk > 0) {
      // Kullanıcı çözünürlüğü: taze voxelle (ince=küçük voxel, yüksek=ağır)
      kafes = voxelle({
        positions: this.sonGeo.positions,
        indices: this.sonGeo.indices,
        bbox: this.sonGeo.bbox,
        cozunurluk: this.dolumCozunurluk,
        hacimCm3Kesin: this.malzemeHacimCm3(),
      });
    } else {
      kafes = this.sonKafes ?? voxelle({
        positions: this.sonGeo.positions,
        indices: this.sonGeo.indices,
        bbox: this.sonGeo.bbox,
        cozunurluk: 64,
        hacimCm3Kesin: this.malzemeHacimCm3(),
      });
      // Akışkan CA performansı için büyük kafesi kabalaştır (hücre ∝ adım maliyeti)
      if (kafes.doluSayi > 500000) kafes = this.kafesKabalastir(kafes, 3);
      else if (kafes.doluSayi > 120000) kafes = this.kafesKabalastir(kafes, 2);
    }
    // Besleyicileri GERÇEK voxel hacmi olarak kafese damgala → dolumda dolar,
    // katılaşmada geç donar, çekmede pipe oluşumu görünür (MAGMA feeding paritesi).
    this.sonBesleyiciler = this._yolluklar.filter(y => y.tip === 'besleyici' && y.kabulEdildi !== false);
    if (this.sonBesleyiciler.length) {
      kafes = kafesGenislet(kafes, this.sonBesleyiciler.map(y => ({
        pozisyon: y.pozisyon.clone(),
        yon: y.yon?.clone() ?? this.yukari.clone(),
        capMm: y.capMm,
        yukseklikMm: y.yukseklikMm,
      })));
    }
    this.sonDolumKafes = kafes; // katılaşma + görsel aynı kafesi kullansın (hizalama)

    const girisler = this._yolluklar
      .filter(y => y.tip === 'dolum_girisi')
      .map(y => ({
        pozisyon: y.pozisyon.clone(), debiGramSn: y.debiGramSn || 100, profil: y.akisProfili ?? 'sabit',
        acilmaSn: y.acilmaSn, kapanmaSn: y.kapanmaSn,
      }));
    // Giriş yoksa: en üst noktadan varsayılan giriş
    if (girisler.length === 0) {
      const ust = this.sonGeo.bbox.getCenter(new THREE.Vector3());
      const half = this.sonGeo.bbox.getSize(new THREE.Vector3());
      ust.addScaledVector(this.yukari, 0.5 * (Math.abs(half.x * this.yukari.x) + Math.abs(half.y * this.yukari.y) + Math.abs(half.z * this.yukari.z)));
      girisler.push({ pozisyon: ust, debiGramSn: 100, profil: 'sabit', acilmaSn: undefined, kapanmaSn: undefined });
    }
    const toplamDebiGramSn = girisler.reduce((t, g) => t + g.debiGramSn, 0);

    // Filtre/maça/hava deliği bölgeleri (konum+çap) yolluklardan
    const bolge = (tip: YollukTipi) => this._yolluklar
      .filter(y => y.tip === tip && y.kabulEdildi !== false)
      .map(y => ({ pozisyon: y.pozisyon.clone(), capMm: y.capMm }));

    // FİNAL kalite: SPH WebGPU parçacık dolumu (köprü ile voxel'e splat).
    // Hata/desteksizlik durumunda CA önizlemeye sessizce düşer.
    if (this.cozumKalitesi === 'final' && typeof navigator !== 'undefined' && (navigator as any).gpu) {
      try {
        const sonucSPH = await this.dolumSimulasyonuSPH(alasim, kafes, girisler, toplamDebiGramSn, ilerle);
        this.sonDolumSonuc = sonucSPH;
        return { kafes, sonuc: sonucSPH };
      } catch (e) {
        console.warn('[FikoDokumKontrol] SPH final dolum başarısız — CA önizlemeye düşülüyor:', e);
      }
    }

    const sonuc = await new FikoDolumSimulasyon().hesapla({
      kafes,
      girisler,
      yukari: this.yukari.clone(),
      akiskanlik: alasim.akiskanlikIndeksi ?? 80,
      toplamDebiGramSn,
      yogunlukGcm3: alasim.yogunlukKati / 1000,
      ilerle,
      filtreler: bolge('filtre'),
      macalar: bolge('maca'),
      havalandirmalar: bolge('hava_deligi'),
      // Non-izotermal akış: metal akarken soğur → cold shut/misrun + katılaşmaya başlangıç T
      termal: {
        dokumSicaklik: this.dokumSicaklik(alasim),
        T_liquidus: alasim.T_liquidus,
        T_solidus: alasim.T_solidus,
        kalipSicaklik: this.kalipSicaklikOverride,
        soguma: 1,
      },
    });

    this.sonDolumSonuc = sonuc; // sensör + katılaşma başlangıç T için sakla
    return { kafes, sonuc };
  }

  /**
   * FİNAL dolum — SPH WebGPU parçacık simülasyonu (Faz 0/1 köprüsü).
   * Akış: kafes→SDF (CPU chamfer, kavite içi +) → gizli canvas'ta FikoSPHMotor
   * headless sabit-adım → her adım köprü splat (parçacık→voxel atomicMin
   * fillTime) → readback → CA formatında IDolumSonuc.
   * Ölçek: dokum kafesi mm, SPH metre (×0.001) — fizik gerçek boyutta.
   */
  private async dolumSimulasyonuSPH(
    alasim: any,
    kafes: IVoxelKafes,
    girisler: { pozisyon: THREE.Vector3; debiGramSn: number; acilmaSn?: number; kapanmaSn?: number }[],
    toplamDebiGramSn: number,
    ilerle?: (y: number) => void,
  ): Promise<IDolumSonuc> {
    const OLCEK = 0.001; // mm → m
    const [nx, ny, nz] = kafes.boyut;
    const rapor = ilerle ?? (() => {});
    rapor(2);

    // 1) SDF: doluluk kafesinden işaretli mesafe (kavite içi +, dışı −)
    const sdf = kafestenSDF(kafes.doluluk, kafes.boyut, kafes.hucreMm, OLCEK);
    const originM: [number, number, number] = [
      kafes.origin.x * OLCEK, kafes.origin.y * OLCEK, kafes.origin.z * OLCEK,
    ];
    const cellM = kafes.hucreMm * OLCEK;
    rapor(8);

    // 2) Parçacık boyutlandırma: kavite hacmi / hedef parçacık sayısı
    const hacimM3 = Math.max(1e-9, kafes.hacimCm3 * 1e-6);
    const hedefParcacik = Math.min(120000, Math.max(15000, kafes.doluSayi));
    const spacing = Math.cbrt(hacimM3 / hedefParcacik);
    const h = spacing * 1.3;
    const rho = Math.max(500, alasim.yogunlukKati ?? 2700); // kg/m³
    const parcacikKutleKg = rho * spacing * spacing * spacing;

    // Sim sınırları: kafes bbox + kenar payı
    const pay = Math.max(4 * h, 4 * cellM);
    const simBounds = {
      min: { x: originM[0] - pay, y: originM[1] - pay, z: originM[2] - pay },
      max: {
        x: originM[0] + nx * cellM + pay,
        y: originM[1] + ny * cellM + pay,
        z: originM[2] + nz * cellM + pay,
      },
    };

    // Zaman: gerçek dolum süresi = kütle / debi. GPU adım bütçesi için sim
    // HIZLANDIRILIR (≤2.5 s sim dolumu) — çıktı zamanları `hizlanma` ile
    // gerçek süreye geri ölçeklenir (dolum SIRASI/deseni korunur).
    const dokumKutleKg = hacimM3 * rho;
    const dolumSuresiSn = Math.max(0.5, dokumKutleKg / Math.max(1e-3, toplamDebiGramSn / 1000));
    const simDolumSure = Math.min(dolumSuresiSn, 2.5);
    const hizlanma = dolumSuresiSn / simDolumSure;
    const yukseklikM = ny * cellM;
    const vMax = Math.max(2, Math.sqrt(2 * 9.81 * Math.max(0.05, yukseklikM)));
    const dt = Math.min(2e-3, Math.max(1.5e-4, 0.25 * h / vMax));

    // 3) Gizli canvas + motor (render başlar ama RAF döngüsü hiç çalışmaz)
    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    const motor = new FikoSPHMotor();
    const kopru = new FikoSPHVoxelKopru();
    try {
      motor.configGuncelle({
        global: {
          maxParticle: hedefParcacik,
          smoothingRadius: h,
          particleRadius: spacing * 0.5,
          restDensity: rho,
          boundaryDamping: 0.1,
          cflFactor: 0.4,
          dt,
          gravity: {
            x: -this.yukari.x * 9.81,
            y: -this.yukari.y * 9.81,
            z: -this.yukari.z * 9.81,
          },
          simBounds,
        },
      } as any);

      const ok = await motor.basla(canvas);
      if (!ok) throw new Error('WebGPU başlatılamadı');
      motor.kapSDFDogrudanYukle(sdf, { origin: originM, cellSize: cellM, boyut: kafes.boyut });

      // Girişler: mm→m, debi g/s → parçacık/s (hızlandırma dahil), hız = debi/kesit
      for (const g of girisler) {
        const rM = Math.max(spacing, ((this._yolluklar.find(y =>
          y.tip === 'dolum_girisi' && y.pozisyon.equals(g.pozisyon))?.capMm ?? 20) / 2) * OLCEK);
        const debiM3Sn = (g.debiGramSn / 1000) / rho * hizlanma;
        const hiz = Math.min(6, Math.max(0.5, debiM3Sn / (Math.PI * rM * rM)));
        motor.dokumGirisiEkle({
          pozisyon: { x: g.pozisyon.x * OLCEK, y: g.pozisyon.y * OLCEK, z: g.pozisyon.z * OLCEK },
          yon: { x: -this.yukari.x, y: -this.yukari.y, z: -this.yukari.z },
          hiz,
          yaricap: rM,
          debi: (g.debiGramSn / 1000) * hizlanma / parcacikKutleKg, // parçacık/s (sim)
          akisBaslangic: (g.acilmaSn ?? 0) / hizlanma,
          akisBitis: (g.kapanmaSn ?? 0) > 0 ? (g.kapanmaSn as number) / hizlanma : 0,
        });
      }

      // 4) Köprü + headless koşu (her adım splat)
      const device = motor.getDevice()!;
      const radiusVoxel = Math.max(1, Math.min(2, Math.round(spacing / cellM)));
      kopru.basla(device, { origin: originM, cellSize: cellM, boyut: kafes.boyut }, radiusVoxel);

      const sureSn = simDolumSure * 1.35 + 0.3; // dolum + çökelme payı
      await motor.hesaplaAdimlar({
        sureSn,
        dt,
        onAdimEncode: (enc, tSn) => {
          const pos = motor.getPositionBuffer();
          const n = motor.getAktifParcacikSayisi();
          if (pos && n > 0) kopru.encodeSplat(enc, pos, n, tSn * 1000);
        },
        ilerle: (p) => rapor(10 + p * 0.85),
      });

      // 5) Readback → IDolumSonuc (sim zamanı × hizlanma = gerçek zaman)
      const fill = await kopru.fillTimeOku();
      if (!fill) throw new Error('fillTime okunamadı');
      return this.sphSonucOlustur(alasim, kafes, fill, hizlanma);
    } finally {
      try { kopru.temizle(); } catch (_) {}
      try { motor.temizle(); } catch (_) {}
      rapor(100);
    }
  }

  /** SPH köprü fillTime (ms) alanını CA `IDolumSonuc` formatına dönüştür */
  private sphSonucOlustur(alasim: any, kafes: IVoxelKafes, fill: Uint32Array, hizlanma = 1): IDolumSonuc {
    const [nx, ny, nz] = kafes.boyut;
    const toplam = nx * ny * nz;
    const nxy = nx * ny;
    const doluluk = kafes.doluluk;

    const dolumZamani = new Float32Array(toplam).fill(Infinity);
    let toplamSure = 0;
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1 || fill[i] === DOLMADI_U32) continue;
      const t = (fill[i] / 1000) * hizlanma; // sim → gerçek zaman
      dolumZamani[i] = t;
      if (t > toplamSure) toplamSure = t;
    }

    // Kapama (closing): parçacık ayrıklığından kalan tekil delikleri komşu
    // çoğunluğuyla doldur (≥4/6 komşu dolu → max komşu zamanı)
    for (let tur = 0; tur < 2; tur++) {
      for (let z = 1; z < nz - 1; z++) {
        for (let y = 1; y < ny - 1; y++) {
          const st = nx * (y + ny * z);
          for (let x = 1; x < nx - 1; x++) {
            const i = st + x;
            if (doluluk[i] !== 1 || isFinite(dolumZamani[i])) continue;
            let say = 0, maxT = 0;
            const komsular = [i - 1, i + 1, i - nx, i + nx, i - nxy, i + nxy];
            for (const k of komsular) {
              const tk = dolumZamani[k];
              if (isFinite(tk)) { say++; if (tk > maxT) maxT = tk; }
            }
            if (say >= 4) dolumZamani[i] = maxT;
          }
        }
      }
    }

    // Dolmayan kavite hücreleri: say + kümele (hava cebi / misrun görünümü)
    let dolmayanSayi = 0;
    const havaCepleri: { merkez: THREE.Vector3; hucreSayisi: number }[] = [];
    const ziyaret = new Uint8Array(toplam);
    const kuyruk = new Int32Array(toplam);
    const tmp = new THREE.Vector3();
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1 || isFinite(dolumZamani[i])) continue;
      dolmayanSayi++;
      if (ziyaret[i]) continue;
      // BFS küme
      let kb = 0, ks = 0;
      kuyruk[kb++] = i; ziyaret[i] = 1;
      let sx = 0, sy = 0, sz = 0, n = 0;
      while (ks < kb) {
        const u = kuyruk[ks++];
        voxelMerkez(kafes, u, tmp);
        sx += tmp.x; sy += tmp.y; sz += tmp.z; n++;
        const ux = u % nx, uy = ((u / nx) | 0) % ny, uz = (u / nxy) | 0;
        const komsu = [
          ux > 0 ? u - 1 : -1, ux < nx - 1 ? u + 1 : -1,
          uy > 0 ? u - nx : -1, uy < ny - 1 ? u + nx : -1,
          uz > 0 ? u - nxy : -1, uz < nz - 1 ? u + nxy : -1,
        ];
        for (const v of komsu) {
          if (v < 0 || ziyaret[v] || doluluk[v] !== 1 || isFinite(dolumZamani[v])) continue;
          ziyaret[v] = 1; kuyruk[kb++] = v;
        }
      }
      if (n >= 4) havaCepleri.push({ merkez: new THREE.Vector3(sx / n, sy / n, sz / n), hucreSayisi: n });
    }
    havaCepleri.sort((a, b) => b.hucreSayisi - a.hucreSayisi);
    havaCepleri.length = Math.min(havaCepleri.length, 8);

    // Varış sıcaklığı yaklaşımı: metal dolum boyunca liquidus'a doğru soğur
    // (geç dolan bölge daha soğuk gelir). Dolmayan → solidus (CA temizliğiyle aynı).
    const tDokum = this.dokumSicaklik(alasim);
    const tLiq = alasim.T_liquidus ?? tDokum - 50;
    const tSol = alasim.T_solidus ?? tLiq - 60;
    const varisSicaklik = new Float32Array(toplam);
    const dusum = (tDokum - tLiq) * 0.9;
    const sureBol = Math.max(1e-3, toplamSure);
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1) continue;
      const t = dolumZamani[i];
      varisSicaklik[i] = isFinite(t) ? tDokum - dusum * Math.min(1, t / sureBol) : tSol;
    }

    return {
      dolumZamani,
      toplamSure,
      havaCepleri,
      sogukBolgeler: [],
      dolmayanSayi,
      varisSicaklik,
    };
  }

  /**
   * Varyant dolum (DOE): verilen döküm sıcaklığıyla dolumu TAHRİBATSIZ çalıştır
   * (state'e dokunmaz) — proses penceresi karşılaştırması için. Önce normal
   * dolum/kontrol çalışmış olmalı (sonDolumKafes gerekir).
   */
  async varyantDolum(alasimId: string, dokumSicaklikC: number): Promise<IDolumSonuc | null> {
    if (!this.sonGeo || !this.sonDolumKafes) return null;
    const alasim = this.alasimCoz(alasimId);
    if (!alasim) return null;

    const girisler = this._yolluklar
      .filter(y => y.tip === 'dolum_girisi')
      .map(y => ({
        pozisyon: y.pozisyon.clone(), debiGramSn: y.debiGramSn || 100, profil: y.akisProfili ?? 'sabit' as const,
        acilmaSn: y.acilmaSn, kapanmaSn: y.kapanmaSn,
      }));
    if (girisler.length === 0) {
      const ust = this.sonGeo.bbox.getCenter(new THREE.Vector3());
      const half = this.sonGeo.bbox.getSize(new THREE.Vector3());
      ust.addScaledVector(this.yukari, 0.5 * (Math.abs(half.x * this.yukari.x) + Math.abs(half.y * this.yukari.y) + Math.abs(half.z * this.yukari.z)));
      girisler.push({ pozisyon: ust, debiGramSn: 100, profil: 'sabit', acilmaSn: undefined, kapanmaSn: undefined });
    }
    const toplamDebiGramSn = girisler.reduce((t, g) => t + g.debiGramSn, 0);
    const bolge = (tip: YollukTipi) => this._yolluklar
      .filter(y => y.tip === tip && y.kabulEdildi !== false)
      .map(y => ({ pozisyon: y.pozisyon.clone(), capMm: y.capMm }));

    return new FikoDolumSimulasyon().hesapla({
      kafes: this.sonDolumKafes,
      girisler,
      yukari: this.yukari.clone(),
      akiskanlik: alasim.akiskanlikIndeksi ?? 80,
      toplamDebiGramSn,
      yogunlukGcm3: alasim.yogunlukKati / 1000,
      filtreler: bolge('filtre'),
      macalar: bolge('maca'),
      havalandirmalar: bolge('hava_deligi'),
      termal: {
        dokumSicaklik: dokumSicaklikC,
        T_liquidus: alasim.T_liquidus,
        T_solidus: alasim.T_solidus,
        kalipSicaklik: this.kalipSicaklikOverride,
      },
    });
  }

  /** Faz 2 — Katılaşma simülasyonu (entalpi FDM). kontrolEt sonrası. */
  async katilasmaSimulasyonu(alasimId: string, kumId: string, baslangicT?: Float32Array, ilerle?: (y: number) => void): Promise<IKatilasmaSonuc | null> {
    // Görselle hizalı olsun diye dolum CA'nın kafesini kullan (yoksa ince kafes)
    const kafes = this.sonDolumKafes ?? this.sonKafes;
    if (!kafes) return null;
    const db = getDokumMalzemeDB();
    const al = this.alasimCoz(alasimId);
    const kum = db.kalipAl(kumId) ?? db.kategoriKaliplar(KalipMalzemeKategori.KUM)[0] ?? db.tumKaliplar()[0];
    if (!al || !kum) return null;

    const sogutucular = this._yolluklar
      .filter(y => y.tip === 'sogutucu' && y.kabulEdildi !== false)
      .map(y => ({
        pozisyon: y.pozisyon.clone(), capMm: y.capMm,
        hCarpan: SOGUTUCU_H_CARPAN[y.sogutucuMalzeme ?? 'celik'],
      }));

    // Besleyici termal davranışları (kafese damgalanan sırayla — idx hizalı)
    const besleyiciler = this.sonBesleyiciler.map((y, idx) => ({
      id: y.id,
      idx,
      hCarpan: BESLEYICI_TIP_H_CARPAN[y.besleyiciTipi ?? 'acik'],
      acik: (y.besleyiciTipi ?? 'acik') === 'acik',
    }));

    // Dolumdan gelen varış sıcaklığı varsa katılaşma başlangıcı olarak kullan (coupling)
    const varis = this.sonDolumSonuc?.varisSicaklik;
    const baslangicTKullan = baslangicT
      ?? (varis && varis.length === kafes.doluluk.length ? varis : undefined);

    const sonuc = await new FikoKatilasmaSimulasyon().hesapla({
      kafes,
      ilerle,
      alasim: {
        yogunlukKgM3: al.yogunlukKati,
        T_solidus: al.T_solidus,
        T_liquidus: al.T_liquidus,
        gizliIsi: al.gizliIsi,
        isiIletkenlik: (T: number) => sicaklikLookup(al.isiIletkenlikEgrisi, T) || 100,
        ozguIsi: (T: number) => sicaklikLookup(al.ozguIsiEgrisi, T) || 1000,
        katiFraksiyon: (T: number) => katilasmaFraksiyonu(al.katilasmaEgrisi, T),
        dokumSicaklik: this.dokumSicaklik(al),
        // Kategoriye göre elastik özellikler → artık gerilim MPa + çarpılma mm tahmini
        elastikModulGpa: KATEGORI_ELASTIK[al.kategori]?.E,
        termalGenlesme: KATEGORI_ELASTIK[al.kategori]?.alfa,
        akmaSicakMpa: KATEGORI_ELASTIK[al.kategori]?.akma,
      },
      kalip: {
        arayuzH: kum.arayuzIsiTransferKatsayisi || 1000,
        baslangicSicaklik: this.kalipSicaklikOverride, // kalıp ön ısıtma (varsayılan 25)
        // Kum ısı penetrasyon katsayısı b=√(k·ρ·c) → semi-infinite kum modeli
        arayuzPenetrasyon: (kum.isiIletkenlik > 0 && kum.yogunluk > 0 && kum.ozguIsi > 0)
          ? Math.sqrt(kum.isiIletkenlik * kum.yogunluk * kum.ozguIsi)
          : 1000,
      },
      sogutucular,
      besleyiciler,
      // Çekme & besleme: alaşımın katılaşma hacim daralması → besleyici pipe + boşluk
      cekmeOrani: al.cekmeOrani ?? 0.05,
      yukari: this.yukari.clone(),
      // Maça gazı (blowhole) analizi: maça çevresi geç katılaşırsa risk
      macalar: this._yolluklar
        .filter(y => y.tip === 'maca' && y.kabulEdildi !== false)
        .map(y => ({ pozisyon: y.pozisyon.clone(), capMm: y.capMm })),
      havaDelikleri: this._yolluklar
        .filter(y => y.tip === 'hava_deligi' && y.kabulEdildi !== false)
        .map(y => ({ pozisyon: y.pozisyon.clone(), capMm: y.capMm })),
      baslangicT: baslangicTKullan,
    });
    this.sonKatilasmaSuresi = sonuc.katilasmaSuresi; // sensör okuması için
    return sonuc;
  }

  yollukAyarla(ayar: { maxBesleyici?: number; besleyiciMaxCapMm?: number }): void {
    this.yollukMotoru.ayarla(ayar);
  }

  yollukOner(): IYollukEleman[] {
    if (!this.sonGosterge || !this.sonGeo) return [];
    this._yolluklar = this.yollukMotoru.oner(this.sonGosterge, this.sonGeo, this.sonAlasim);
    return this._yolluklar;
  }

  methodingHesapla(alasimId: string, kumId: string, manuelHacimCm3?: number): IMethodingSonuc | null {
    if (!this.sonGeo) return null;
    const db = getDokumMalzemeDB();
    const alasim = this.alasimCoz(alasimId);
    const kum = db.kalipAl(kumId)
      ?? db.kategoriKaliplar(KalipMalzemeKategori.KUM)[0]
      ?? db.tumKaliplar()[0];
    if (!alasim || !kum) return null;

    // Hedef kesit modülü: en kalın sıcak nokta bölgesinden (mm → cm /2/10)
    let hedefKalinlikMm = this.sonGosterge?.ortKalinlik ?? 10;
    const sicaklar = this.sonGosterge?.bolgeler.filter(b => b.tip === 'sicak_nokta') ?? [];
    if (sicaklar.length) hedefKalinlikMm = Math.max(...sicaklar.map(b => b.deger || hedefKalinlikMm));
    const hedefKesitModulCm = Math.max(0.05, hedefKalinlikMm / 2 / 10);

    return methodingHesaplaFn({
      positions: this.sonGeo.positions,
      indices: this.sonGeo.indices,
      alasim, kum,
      yolluklar: this._yolluklar,
      hedefKesitModulCm,
      hacimCm3Override: (manuelHacimCm3 && manuelHacimCm3 > 0) ? manuelHacimCm3 : this.malzemeHacimCm3(),
    });
  }

  yollukEkle(tip: YollukTipi, pozisyon: THREE.Vector3, yon?: THREE.Vector3): IYollukEleman {
    const varsayilanYon = tip === 'dolum_girisi' ? new THREE.Vector3(0, -1, 0) : new THREE.Vector3(0, 1, 0);
    // Tip bazlı varsayılan çap (sensör küçük işaret, maça/soğutucu geniş etki)
    const varCap = tip === 'sensor' ? 10 : tip === 'maca' ? 30 : tip === 'sogutucu' ? 25 : 20;
    const eleman: IYollukEleman = {
      id: `${tip}-manuel-${this._yolluklar.length}`,
      tip,
      pozisyon: pozisyon.clone(),
      yon: (yon && yon.lengthSq() > 1e-6 ? yon.clone().normalize() : varsayilanYon),
      capMm: varCap,
      yukseklikMm: 30,
      debiGramSn: tip === 'dolum_girisi' ? 100 : undefined,
      akisProfili: tip === 'dolum_girisi' ? 'sabit' : undefined,
      besleyiciTipi: tip === 'besleyici' ? 'acik' : undefined,
      sogutucuMalzeme: tip === 'sogutucu' ? 'celik' : undefined,
      otomatikMi: false,
      kabulEdildi: true,
      gecerli: true,
      dogrulamaMesaji: '',
    };
    if (this.sonGosterge && this.sonGeo) {
      const d = this.yollukMotoru.dogrula(eleman, this.sonGosterge, this.sonGeo);
      eleman.gecerli = d.gecerli;
      eleman.dogrulamaMesaji = d.mesaj;
    }
    this._yolluklar = [...this._yolluklar, eleman];
    return eleman;
  }

  yollukYonGuncelle(id: string, yon: THREE.Vector3): IYollukEleman | null {
    return this.yollukGuncelle(id, { yon });
  }

  yollukGuncelle(id: string, kismi: IYollukGuncelleme): IYollukEleman | null {
    const e = this._yolluklar.find(y => y.id === id);
    if (!e) return null;
    if (kismi.pozisyon) e.pozisyon = kismi.pozisyon.clone();
    if (kismi.yon && kismi.yon.lengthSq() > 1e-6) e.yon = kismi.yon.clone().normalize();
    if (kismi.capMm != null && kismi.capMm > 0) e.capMm = kismi.capMm;
    if (kismi.yukseklikMm != null && kismi.yukseklikMm > 0) e.yukseklikMm = kismi.yukseklikMm;
    if (kismi.debiGramSn != null && kismi.debiGramSn > 0) e.debiGramSn = kismi.debiGramSn;
    if (kismi.akisProfili) e.akisProfili = kismi.akisProfili;
    if (kismi.acilmaSn != null) e.acilmaSn = Math.max(0, kismi.acilmaSn);
    if (kismi.kapanmaSn != null) e.kapanmaSn = kismi.kapanmaSn > 0 ? kismi.kapanmaSn : undefined;
    if (kismi.besleyiciTipi) e.besleyiciTipi = kismi.besleyiciTipi;
    if (kismi.sogutucuMalzeme) e.sogutucuMalzeme = kismi.sogutucuMalzeme;
    if (this.sonGosterge && this.sonGeo) {
      const d = this.yollukMotoru.dogrula(e, this.sonGosterge, this.sonGeo);
      e.gecerli = d.gecerli;
      e.dogrulamaMesaji = d.mesaj;
    }
    this._yolluklar = [...this._yolluklar];
    return e;
  }

  /** Sensör yolluklarının ölçümlerini son dolum+katılaşma sonuçlarından doldur. */
  sensorleriOku(): void {
    const kafes = this.sonDolumKafes;
    for (const y of this._yolluklar) {
      if (y.tip !== 'sensor') continue;
      const v: ISensorVeri = {};
      if (kafes) {
        const lin = this.kafesteDoluLin(kafes, y.pozisyon);
        if (lin >= 0) {
          if (this.sonDolumSonuc) {
            const f = this.sonDolumSonuc.dolumZamani[lin];
            if (isFinite(f)) v.dolumSn = f;
          }
          if (this.sonKatilasmaSuresi && this.sonKatilasmaSuresi[lin] > 0) {
            v.katilasmaSn = this.sonKatilasmaSuresi[lin];
          }
          if (kafes.kalinlik[lin] > 0) v.kalinlikMm = kafes.kalinlik[lin];
        }
      }
      y.sensorVeri = v;
    }
    this._yolluklar = [...this._yolluklar];
  }

  /** Noktanın kafes lineer indeksi; dolu değilse küçük komşulukta en yakın doluyu ara. */
  private kafesteDoluLin(kafes: IVoxelKafes, p: THREE.Vector3): number {
    let lin = voxelIndeks(kafes, p);
    if (lin >= 0 && kafes.doluluk[lin] === 1) return lin;
    const [nx, ny, nz] = kafes.boyut;
    const h = kafes.hucreMm;
    const ix = Math.round((p.x - kafes.origin.x) / h - 0.5);
    const iy = Math.round((p.y - kafes.origin.y) / h - 0.5);
    const iz = Math.round((p.z - kafes.origin.z) / h - 0.5);
    for (let r = 1; r <= 3; r++) {
      for (let dz = -r; dz <= r; dz++) {
        const z = iz + dz; if (z < 0 || z >= nz) continue;
        for (let dy = -r; dy <= r; dy++) {
          const yy = iy + dy; if (yy < 0 || yy >= ny) continue;
          for (let dx = -r; dx <= r; dx++) {
            const x = ix + dx; if (x < 0 || x >= nx) continue;
            const l = x + nx * (yy + ny * z);
            if (kafes.doluluk[l] === 1) return l;
          }
        }
      }
    }
    return -1;
  }

  yollukKabul(id: string): IYollukEleman | null {
    const e = this._yolluklar.find(y => y.id === id);
    if (!e) return null;
    e.kabulEdildi = true;
    if (this.sonGosterge && this.sonGeo) {
      const d = this.yollukMotoru.dogrula(e, this.sonGosterge, this.sonGeo);
      e.gecerli = d.gecerli;
      e.dogrulamaMesaji = d.mesaj;
    }
    this._yolluklar = [...this._yolluklar];
    return e;
  }

  yollukSil(id: string): void {
    this._yolluklar = this._yolluklar.filter(y => y.id !== id);
  }

  yolluklar(): IYollukEleman[] {
    return this._yolluklar;
  }

  dispose(): void {
    this.sonGosterge = null;
    this.sonGeo = null;
    this._yolluklar = [];
  }

  // ───────────────────────────────────────────────────────────────────────

  private minCidar(alasim: string): number {
    return ALASIM_MIN_CIDAR[alasim] ?? 2.5;
  }

  /** Meshleri tek bir dünya-uzayı indeksli geometride birleştir */
  private meshleriBirlestir(meshler: THREE.Mesh[]): { mesh: THREE.Mesh; geo: ICozumGeometri } {
    const posList: number[] = [];
    const normList: number[] = [];
    const idxList: number[] = [];
    let vertexOfset = 0;
    const v = new THREE.Vector3();
    const n = new THREE.Vector3();
    const normalMat = new THREE.Matrix3();

    for (const mesh of meshler) {
      const geo = mesh.geometry as THREE.BufferGeometry;
      if (!geo?.attributes?.position) continue;
      mesh.updateWorldMatrix(true, false);
      const dunyaMat = mesh.matrixWorld;
      normalMat.getNormalMatrix(dunyaMat);

      let kaynak = geo;
      if (!geo.attributes.normal) {
        kaynak = geo.clone();
        kaynak.computeVertexNormals();
      }
      const pos = kaynak.attributes.position;
      const nor = kaynak.attributes.normal;
      const vSay = pos.count;

      for (let i = 0; i < vSay; i++) {
        v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(dunyaMat);
        posList.push(v.x, v.y, v.z);
        n.set(nor.getX(i), nor.getY(i), nor.getZ(i)).applyMatrix3(normalMat).normalize();
        normList.push(n.x, n.y, n.z);
      }

      if (kaynak.index) {
        const idx = kaynak.index;
        for (let i = 0; i < idx.count; i++) idxList.push(idx.getX(i) + vertexOfset);
      } else {
        for (let i = 0; i < vSay; i++) idxList.push(i + vertexOfset);
      }
      vertexOfset += vSay;
      if (kaynak !== geo) kaynak.dispose();
    }

    const positions = new Float32Array(posList);
    const normals = new Float32Array(normList);
    const indices = new Uint32Array(idxList);

    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.setIndex(new THREE.BufferAttribute(indices, 1));
    merged.computeBoundingBox();

    const mesh = new THREE.Mesh(merged, new THREE.MeshBasicMaterial());
    const bbox = merged.boundingBox!.clone();

    const geo: ICozumGeometri = {
      positions, normals, indices,
      vertexSayisi: positions.length / 3,
      bbox,
    };
    return { mesh, geo };
  }

  private birlesikTemizle(mesh: THREE.Mesh): void {
    try {
      (mesh.geometry as THREE.BufferGeometry).dispose();
      (mesh.material as THREE.Material).dispose();
    } catch (_) {}
  }

  /**
   * Cidar kalınlığı + tavan mesafesi — TEK MeshBVH, iki sıkı raycastFirst geçişi.
   * THREE.Raycaster yerine doğrudan bvh.raycastFirst (dizi/sort yok) → 10-50× hızlı.
   * kalınlık[i] = i. vertex'ten -normal yönünde karşı yüzeye mesafe.
   * tavan[i]    = i. vertex'ten +Y yönünde üstteki yüzeye mesafe (hapsolan hava).
   */
  private async bvhAlanlariHesapla(
    geo: ICozumGeometri, maxDistance: number, kafes: IVoxelKafes | null, ilerle: (y: number) => void,
  ): Promise<{ kalinlik: Float32Array; tavan: Float32Array | undefined }> {
    const n = geo.vertexSayisi;
    const kalinlik = new Float32Array(n);

    let bvh: MeshBVH;
    try {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(geo.positions, 3));
      g.setIndex(new THREE.BufferAttribute(geo.indices, 1));
      bvh = new MeshBVH(g);
    } catch (_) {
      kalinlik.fill(Math.max(1, Math.min(geo.bbox.getSize(new THREE.Vector3()).x, maxDistance) * 0.1));
      return { kalinlik, tavan: undefined };
    }

    const tavan = new Float32Array(n);
    const pos = geo.positions, nor = geo.normals;
    const eps = Math.max(1e-4, maxDistance * 1e-5);
    const ray = new THREE.Ray();
    const yukari = (geo.yukariYon ?? new THREE.Vector3(0, 1, 0)).clone().normalize();
    const yild = Math.max(1, Math.floor(n / 12)); // ~12 nefes
    const ic = new THREE.Vector3();
    const ofset = kafes ? kafes.hucreMm * 1.3 : eps; // yüzeyden içeri kaydır

    for (let i = 0; i < n; i++) {
      const ix = i * 3;
      const px = pos[ix], py = pos[ix + 1], pz = pos[ix + 2];
      let nx = nor[ix], ny = nor[ix + 1], nz = nor[ix + 2];
      const nl = Math.hypot(nx, ny, nz) || 1; nx /= nl; ny /= nl; nz /= nl;

      // Kalınlık: voxel SDF'ten örnekle (iç+dış yüz AYNI duvar voxeline düşer → TUTARLI,
      // gerçek duvar kalınlığı = 2×en yakın yüzey mesafesi).
      let k = 0;
      if (kafes) {
        ic.set(px - nx * ofset, py - ny * ofset, pz - nz * ofset); // içeri kaydır
        let lin = voxelIndeks(kafes, ic);
        if (lin < 0 || !kafes.doluluk[lin]) { // ters normal? +normal dene
          ic.set(px + nx * ofset, py + ny * ofset, pz + nz * ofset);
          lin = voxelIndeks(kafes, ic);
        }
        if (lin >= 0 && kafes.doluluk[lin]) k = kafes.kalinlik[lin];
      }
      if (k <= 0) { // yedek: çift yönlü ışın
        ray.origin.set(px - nx * eps, py - ny * eps, pz - nz * eps);
        ray.direction.set(-nx, -ny, -nz);
        const hNeg = bvh.raycastFirst(ray, THREE.DoubleSide as any, 0, maxDistance);
        ray.origin.set(px + nx * eps, py + ny * eps, pz + nz * eps);
        ray.direction.set(nx, ny, nz);
        const hPos = bvh.raycastFirst(ray, THREE.DoubleSide as any, 0, maxDistance);
        k = Math.min(hNeg ? hNeg.distance : maxDistance, hPos ? hPos.distance : maxDistance);
      }
      kalinlik[i] = k;

      // Tavan: yukarı (yerçekiminin tersi) yönünde
      ray.origin.set(px + yukari.x * eps, py + yukari.y * eps, pz + yukari.z * eps);
      ray.direction.copy(yukari);
      const h2 = bvh.raycastFirst(ray, THREE.DoubleSide as any, 0, maxDistance);
      tavan[i] = h2 ? h2.distance : maxDistance;

      if (i % yild === 0) { ilerle(58 + (i / n) * 22); await new Promise(r => setTimeout(r, 0)); }
    }

    return { kalinlik, tavan };
  }

  /**
   * Voxel kafesini faktör kadar kabalaştır (down-sample) — akışkan CA hızı için.
   * Kaba hücre, içindeki herhangi bir ince hücre doluysa dolu; kalınlık = max.
   * Hacim ve origin korunur; hucreMm ×faktör.
   */
  private kafesKabalastir(k: IVoxelKafes, faktor: number): IVoxelKafes {
    const [nx, ny, nz] = k.boyut;
    const mx = Math.ceil(nx / faktor), my = Math.ceil(ny / faktor), mz = Math.ceil(nz / faktor);
    const mToplam = mx * my * mz;
    const doluluk = new Uint8Array(mToplam);
    const kalinlik = new Float32Array(mToplam);
    const sDoluluk = k.doluluk, sKalinlik = k.kalinlik;
    for (let z = 0; z < nz; z++) {
      const cz = (z / faktor) | 0;
      for (let y = 0; y < ny; y++) {
        const cy = (y / faktor) | 0;
        const satir = nx * (y + ny * z);
        const cSatirTb = mx * (cy + my * cz);
        for (let x = 0; x < nx; x++) {
          if (sDoluluk[satir + x] !== 1) continue;
          const c = ((x / faktor) | 0) + cSatirTb;
          doluluk[c] = 1;
          const kv = sKalinlik[satir + x];
          if (kv > kalinlik[c]) kalinlik[c] = kv;
        }
      }
    }
    let doluSayi = 0;
    for (let i = 0; i < mToplam; i++) if (doluluk[i] === 1) doluSayi++;
    return {
      boyut: [mx, my, mz],
      hucreMm: k.hucreMm * faktor,
      origin: k.origin.clone(),
      doluluk,
      doluSayi,
      kalinlik,
      hacimCm3: k.hacimCm3,
    };
  }

  /**
   * Malzeme hacmi (cm³). ÖNCELİK: mesh'ten yukarı çıkıp FikoCizimYalin
   * elemanının `.hacim` getter'ı / `userData.nativeVolumeCm3` (OpenCascade
   * import'unun KESİN hacmi — CATIA ile birebir). Bulunamazsa yedek olarak
   * `hesaplaHacim` × worldScale pipeline'ı (su geçirmez mesh ister).
   */
  private malzemeHacimCm3(): number {
    const gorulen = new Set<string>();
    let toplam = 0, bulundu = false;
    for (const mesh of this.sonMeshler) {
      let o: any = mesh;
      while (o) {
        let deger: number | undefined;
        const nv = o.userData?.['nativeVolumeCm3'];
        if (typeof nv === 'number' && nv > 0) deger = nv;
        else { try { if (typeof o.hacim === 'number' && o.hacim > 0) deger = o.hacim; } catch (_) {} }
        if (deger !== undefined) {
          if (!gorulen.has(o.uuid)) { gorulen.add(o.uuid); toplam += deger; bulundu = true; }
          break;
        }
        o = o.parent;
      }
    }
    if (bulundu) return toplam;
    return this.hesaplaHacimYedek();
  }

  /** Yedek: native hacim yoksa hesaplaHacim × worldScale (parça başına) */
  private hesaplaHacimYedek(): number {
    const _pos = new THREE.Vector3(), _quat = new THREE.Quaternion(), _scl = new THREE.Vector3();
    let toplamCm3 = 0;
    for (const mesh of this.sonMeshler) {
      const g0 = mesh?.geometry as THREE.BufferGeometry;
      if (!g0?.attributes?.position) continue;
      let geom = g0; let gecici: THREE.BufferGeometry | null = null;
      if (g0.index) { gecici = g0.toNonIndexed(); geom = gecici; }
      try {
        let h = hesaplaHacim(geom) * 100000;
        mesh.updateWorldMatrix(true, false);
        mesh.matrixWorld.decompose(_pos, _quat, _scl);
        h = h * Math.abs(_scl.x * _scl.y * _scl.z) / 1000;
        if (isFinite(h)) toplamCm3 += h;
      } catch (_) {}
      if (gecici) gecici.dispose();
    }
    return toplamCm3;
  }
}
